// Email service for sending earthquake alerts
// This is a conceptual implementation - in a real application, 
// this would connect to an email service provider like SendGrid, AWS SES, or similar

class EmailService {
  constructor() {
    // Configuration would come from environment variables in a real implementation
    this.config = {
      apiKey: process.env.EMAIL_API_KEY || 'test-key',
      fromAddress: process.env.EMAIL_FROM_ADDRESS || 'alerts@iligan-risk-portal.com',
      serviceName: process.env.EMAIL_SERVICE || 'test-service'
    };
    
    // In-memory storage for subscriptions (would be a database in production)
    this.subscriptions = new Map();
  }

  /**
   * Subscribe a user to earthquake alerts
   * @param {string} email - User's email address
   * @param {Object} location - User's location object with lat/lng
   * @param {Object} preferences - User's alert preferences
   * @returns {Object} Subscription confirmation
   */
  async subscribeUser(email, location, preferences = {}) {
    try {
      // Validate email format
      if (!this.validateEmail(email)) {
        throw new Error('Invalid email address');
      }
      
      // Default preferences
      const defaultPreferences = {
        magnitudeThreshold: 5.0,
        radiusKm: 100,
        frequency: 'immediate',
        types: ['earthquake']
      };
      
      const subscription = {
        id: this.generateId(),
        email,
        location,
        preferences: { ...defaultPreferences, ...preferences },
        createdAt: new Date().toISOString(),
        lastSent: null,
        isActive: true
      };
      
      // Store subscription (in memory for this example)
      this.subscriptions.set(email, subscription);
      
      // Send confirmation email
      await this.sendConfirmationEmail(email, subscription);
      
      return {
        success: true,
        message: 'Successfully subscribed to earthquake alerts',
        subscriptionId: subscription.id
      };
    } catch (error) {
      console.error('Subscription error:', error);
      return {
        success: false,
        message: error.message || 'Failed to subscribe'
      };
    }
  }

  /**
   * Unsubscribe a user from alerts
   * @param {string} email - User's email address
   * @returns {Object} Unsubscription confirmation
   */
  async unsubscribeUser(email) {
    try {
      if (this.subscriptions.has(email)) {
        const subscription = this.subscriptions.get(email);
        subscription.isActive = false;
        subscription.unsubscribedAt = new Date().toISOString();
        
        // In a real implementation, we would actually delete or mark as inactive
        // this.subscriptions.delete(email);
        
        // Send goodbye email
        await this.sendGoodbyeEmail(email);
        
        return {
          success: true,
          message: 'Successfully unsubscribed from alerts'
        };
      } else {
        return {
          success: false,
          message: 'Email not found in subscription list'
        };
      }
    } catch (error) {
      console.error('Unsubscription error:', error);
      return {
        success: false,
        message: 'Failed to unsubscribe'
      };
    }
  }

  /**
   * Send an earthquake alert to subscribed users
   * @param {Object} earthquake - Earthquake data
   */
  async sendEarthquakeAlert(earthquake) {
    try {
      const subscribers = this.getRelevantSubscribers(earthquake);
      
      if (subscribers.length === 0) {
        console.log('No relevant subscribers for this earthquake');
        return { success: true, message: 'No subscribers to notify' };
      }
      
      console.log(`Sending alerts to ${subscribers.length} subscribers`);
      
      // In a real implementation, we would send emails in batches
      // For this example, we'll simulate the process
      const results = await Promise.allSettled(
        subscribers.map(subscriber => 
          this.sendAlertEmail(subscriber.email, earthquake, subscriber)
        )
      );
      
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;
      
      return {
        success: true,
        message: `Alerts sent: ${successful} successful, ${failed} failed`,
        stats: { successful, failed }
      };
    } catch (error) {
      console.error('Error sending earthquake alerts:', error);
      return {
        success: false,
        message: 'Failed to send earthquake alerts'
      };
    }
  }

  /**
   * Get subscribers who should receive an alert for a specific earthquake
   * @param {Object} earthquake - Earthquake data
   * @returns {Array} List of relevant subscribers
   */
  getRelevantSubscribers(earthquake) {
    const relevantSubscribers = [];
    
    for (const [email, subscription] of this.subscriptions) {
      if (!subscription.isActive) continue;
      
      // Check if earthquake meets subscriber's magnitude threshold
      if (earthquake.magnitude < subscription.preferences.magnitudeThreshold) {
        continue;
      }
      
      // Check if earthquake is within subscriber's radius
      if (subscription.location) {
        const distance = this.calculateDistance(
          earthquake.lat,
          earthquake.lng,
          subscription.location.lat,
          subscription.location.lng
        );
        
        if (distance > subscription.preferences.radiusKm) {
          continue;
        }
      }
      
      relevantSubscribers.push(subscription);
    }
    
    return relevantSubscribers;
  }

  /**
   * Send confirmation email to new subscriber
   * @param {string} email - Subscriber's email
   * @param {Object} subscription - Subscription details
   */
  async sendConfirmationEmail(email, subscription) {
    // In a real implementation, this would use an email service
    console.log(`Sending confirmation email to ${email}`);
    
    // Simulate email sending
    return new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Send goodbye email to unsubscribed user
   * @param {string} email - User's email
   */
  async sendGoodbyeEmail(email) {
    console.log(`Sending goodbye email to ${email}`);
    return new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Send earthquake alert email
   * @param {string} email - Recipient's email
   * @param {Object} earthquake - Earthquake data
   * @param {Object} subscription - Subscription details
   */
  async sendAlertEmail(email, earthquake, subscription) {
    console.log(`Sending alert email to ${email} for earthquake M${earthquake.magnitude}`);
    
    // Simulate email sending
    return new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} Validity of email
   */
  validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  /**
   * Calculate distance between two points (Haversine formula)
   * @param {number} lat1 - Latitude of point 1
   * @param {number} lon1 - Longitude of point 1
   * @param {number} lat2 - Latitude of point 2
   * @param {number} lon2 - Longitude of point 2
   * @returns {number} Distance in kilometers
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    return R * c;
  }

  /**
   * Generate a unique ID
   * @returns {string} Unique ID
   */
  generateId() {
    return 'sub_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Get subscription statistics
   * @returns {Object} Statistics about subscriptions
   */
  getStats() {
    const activeSubscriptions = Array.from(this.subscriptions.values())
      .filter(sub => sub.isActive).length;
      
    return {
      totalSubscriptions: this.subscriptions.size,
      activeSubscriptions,
      service: this.config.serviceName
    };
  }
}

// Export a singleton instance
export default new EmailService();